import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { bookingAPI, vehicleAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import toast from 'react-hot-toast';
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  CreditCardIcon,
  LockOpenIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { ComingSoonPage } from '../../components/ComingSoon';

function ReservationsPage() {
  return (
    <ComingSoonPage
      feature="My Reservations"
      description="View and manage your vehicle reservations. This feature is currently under development."
      icon={<CalendarIcon className="h-10 w-10 text-gray-400" />}
    />
  );
}

/* --- Original implementation preserved below for re-enablement --- */
function _ReservationsPage_Original() {
  const [selectedStatus, setSelectedStatus] = useState('all');
  const queryClient = useQueryClient();

  const { data: reservations, isLoading } = useQuery(
    ['reservations', selectedStatus],
    () => bookingAPI.getReservations(selectedStatus === 'all' ? {} : { status: selectedStatus }),
    { select: (response) => response.data }
  );

  const unlockMutation = useMutation(
    vehicleAPI.unlockVehicle,
    {
      onSuccess: () => {
        toast.success('Vehicle unlocked successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to unlock vehicle');
      }
    }
  );

  const cancelMutation = useMutation(
    bookingAPI.cancelReservation,
    {
      onSuccess: () => {
        toast.success('Reservation cancelled successfully');
        queryClient.invalidateQueries('reservations');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to cancel reservation');
      }
    }
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'active': return 'bg-purple-100 text-purple-800';
      default: return 'bg-surface-container-low text-on-surface';
    }
  };

  const canUnlock = (reservation: any) => {
    const now = new Date();
    const startTime = new Date(reservation.startDate);
    const endTime = new Date(reservation.endDate);
    return reservation.status === 'confirmed' && now >= startTime && now <= endTime;
  };

  const canCancel = (reservation: any) => {
    return ['pending', 'confirmed'].includes(reservation.status);
  };

  const statusFilters = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  if (isLoading) {
    return (
      <PageLayout>
        <LoadingSpinner className="py-20" />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-on-surface">My Reservations</h1>
        <p className="mt-2 text-gray-600">Manage your vehicle bookings</p>
      </div>

      {/* Status Filter */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {statusFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setSelectedStatus(filter.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedStatus === filter.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-highest'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Reservations List */}
      {reservations?.length > 0 ? (
        <div className="space-y-6">
          {reservations.map((reservation: any) => (
            <div key={reservation.id} className="card">
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  {/* Reservation Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-on-surface">
                          {reservation.vehicle?.make} {reservation.vehicle?.model}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Reservation #{reservation.id.substring(0, 8)}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(reservation.status)}`}>
                        {reservation.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-on-surface">Pickup</p>
                          <p className="text-sm text-gray-600">
                            {new Date(reservation.startDate).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(reservation.startDate).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-on-surface">Return</p>
                          <p className="text-sm text-gray-600">
                            {new Date(reservation.endDate).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(reservation.endDate).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <CreditCardIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-on-surface">Total</p>
                          <p className="text-sm text-gray-600">${reservation.totalPrice}</p>
                          {reservation.depositAmount && (
                            <p className="text-xs text-gray-500">
                              Deposit: ${reservation.depositAmount}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {reservation.vehicle?.location && (
                      <div className="flex items-center space-x-2 mb-4">
                        <MapPinIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-sm text-gray-600">{reservation.vehicle.location}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col lg:flex-row gap-2 mt-4 lg:mt-0 lg:ml-6">
                    {canUnlock(reservation) && (
                      <button
                        onClick={() => unlockMutation.mutate(reservation.vehicleId)}
                        disabled={unlockMutation.isLoading}
                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        {unlockMutation.isLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            <LockOpenIcon className="h-4 w-4" />
                            <span>Unlock</span>
                          </>
                        )}
                      </button>
                    )}

                    <button className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      <EyeIcon className="h-4 w-4" />
                      <span>View Details</span>
                    </button>

                    {canCancel(reservation) && (
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to cancel this reservation?')) {
                            cancelMutation.mutate(reservation.id);
                          }
                        }}
                        disabled={cancelMutation.isLoading}
                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        {cancelMutation.isLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            <XMarkIcon className="h-4 w-4" />
                            <span>Cancel</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Special Messages */}
                {reservation.status === 'pending' && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      ⏳ Payment pending. Complete payment to confirm your reservation.
                    </p>
                  </div>
                )}

                {reservation.status === 'confirmed' && canUnlock(reservation) && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      🚗 Your vehicle is ready! You can unlock it now.
                    </p>
                  </div>
                )}

                {reservation.expiresAt && reservation.status === 'pending' && (
                  <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-800">
                      ⚠️ This reservation will expire on {new Date(reservation.expiresAt).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📅</div>
          <h3 className="text-lg font-medium text-on-surface mb-2">No reservations found</h3>
          <p className="text-gray-600 mb-6">
            {selectedStatus === 'all' 
              ? "You haven't made any reservations yet."
              : `No ${selectedStatus} reservations found.`
            }
          </p>
          <button 
            onClick={() => window.location.href = '/booking'}
            className="btn-primary"
          >
            Book Your First Vehicle
          </button>
        </div>
      )}
    </PageLayout>
  );
}

export default ReservationsPage;